// chat/view — chat's React projection (chat-module-plan S7): the conversation
// surface (`ChatFullView`) and the nascent hero composer's chat wiring
// (`nascentComposer`). Pure: it renders the chat Model, reads the facts other
// owners hold through a read-only `ChatViewContext`, and maps every gesture to
// a chat command (`send`), a chat intent (`intents`, whose payload facts the
// root resolved), or a request to another owner (`host`).

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { composerAttachmentsFor } from "../../app/composerAttachments.js";
import type { IngestFile, IngestId } from "../../app/ingest.js";
import { shouldShowOpeningMoveOffer } from "../../app/openingMoveOffer.js";
import {
    chipStatusOf,
    type SourceReadStatus,
} from "../../app/sourceReadStatus.js";
import { transcriptHasCreatedMaterial } from "../../app/turnStream.js";
import type { OpeningMoveOfferView } from "../../components/Chat/ChatConversation/ChatConversation.js";
import { ChatFull } from "../../components/Chat/ChatFull/ChatFull.js";
import type { ResolveInlineLink } from "../../components/Chat/InlineLink/InlineLink.js";
import type { ChatAnchor } from "../../components/Chat/presentation.js";
import { lastTurn } from "../../components/Chat/thread.js";
import type {
    ChatEntry,
    InterruptionAction,
} from "../../components/Chat/types.js";
import { ComposerAttachButton } from "../../components/Composer/PowerComposer/ComposerAttachButton.js";
import type { PowerComposerStatus } from "../../components/Composer/PowerComposer/sendState.js";
import type {
    ComposerResourceRef,
    ComposerSlashCommand,
} from "../../components/Composer/PowerComposer/types.js";
import { mentionsToContextItems } from "../../components/ContextCard/types.js";
import type {
    FocusTarget,
    ResourceId,
    ResourceRef,
    SourceRef,
} from "../../components/Navigation/types.js";
import type { ChatCommand } from "./command.js";
import type { ChatIntents } from "./intents.js";
import {
    type ChatSession,
    chatSessionFor,
    draftingPhaseOf,
    type TurnFailure,
} from "./model.js";
import { sentReferences } from "./sentReferences.js";
import type { ChatState } from "./state.js";

// Read-only facts other owners hold, projected in by the root.
export type ChatViewContext = {
    readonly scope: Parameters<typeof ChatFull>[0]["scope"];
    readonly anchor: ChatAnchor;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    // A source's live read status (the Library's), for its chips (FR-3.3).
    readonly readStatusOf: (id: ResourceId) => SourceReadStatus | undefined;
    readonly resolveLink: ResolveInlineLink;
    readonly sourceItems: readonly SourceRef[];
    readonly activeRef: ResourceRef | null;
    readonly composerCandidates: readonly ComposerResourceRef[];
    readonly chatSlashCommands: readonly ComposerSlashCommand[];
    readonly historicalPlayback: boolean;
    readonly ingest: readonly IngestFile[];
    readonly ingestConversations: ReadonlyMap<IngestId, string>;
    // Localizes a turn failure (chat holds no user-facing strings).
    readonly failureText: (failure: TurnFailure) => string;
};

// Requests to owners that are not modules yet.
export type ChatViewHost = {
    readonly openFilePicker: () => void;
    readonly changeMentions: (next: readonly ComposerResourceRef[]) => void;
    readonly goToChange: (
        entryId: string,
        entries: readonly ChatEntry[],
    ) => void;
    readonly downloadExport: (exportId: ResourceId) => void;
    readonly requestUpload: (
        sourceId: string,
        sourceTitle: string | undefined,
        conversationId: string,
    ) => void;
    // Interruption recoveries owned outside chat (FR-2.5): a fresh
    // conversation (F-07) and a new session (F-10).
    readonly startNewConversation: () => void;
    readonly signInAgain: () => void;
};

// An interruption notice's action → the chat intent or host request that
// performs it. `continue` and the no-history `try_again` send a short author
// message (localized by the caller) so the assistant picks the work up again.
const interruptionHandler =
    (
        conversationId: string,
        session: ChatSession,
        intents: Pick<ChatIntents, "submitText" | "retryTurn" | "stopTurn">,
        host: Pick<ChatViewHost, "startNewConversation" | "signInAgain">,
        prompts: { readonly continue: string; readonly tryAgain: string },
    ) =>
    (action: InterruptionAction): void => {
        switch (action) {
            case "continue":
                intents.submitText(prompts.continue, conversationId);
                return;
            case "try_again":
                if (session.lastSent.type === "nothing_sent")
                    intents.submitText(prompts.tryAgain, conversationId);
                else intents.retryTurn(conversationId);
                return;
            case "stop":
                intents.stopTurn(conversationId);
                return;
            case "new_conversation":
                host.startNewConversation();
                return;
            case "sign_in":
                host.signInAgain();
                return;
            default:
                exhaustSwitchCase(action);
        }
    };

type ChatViewIntents = Pick<
    ChatIntents,
    | "submitText"
    | "retryTurn"
    | "stopTurn"
    | "acceptOpeningMove"
    | "supplyFormAnswer"
    | "acceptProposal"
    | "discardProposal"
>;

// The composer's send state: streaming while a turn runs, the localized
// failure (Retry armed) after one failed, idle otherwise.
const composerStatus = (
    session: ChatSession,
    failureText: (failure: TurnFailure) => string,
): PowerComposerStatus =>
    session.drafting
        ? { type: "streaming" }
        : session.turnError !== null
          ? { type: "error", message: failureText(session.turnError) }
          : { type: "idle" };

// The empty (nascent) conversation's hero composer: the same draft / send /
// stop / retry wiring the full surface has.
export type NascentComposer = {
    readonly draft: string;
    readonly onDraftChange: (text: string) => void;
    readonly onStartThread: () => void;
    readonly composerStatus: PowerComposerStatus;
    readonly composerDisabled: boolean;
    readonly onStop: () => void;
    readonly onRetry: () => void;
};

export const nascentComposer = (
    conversationId: string,
    session: ChatSession,
    context: Pick<ChatViewContext, "historicalPlayback" | "failureText">,
    intents: Pick<ChatIntents, "submitText" | "stopTurn" | "retryTurn">,
    send: (command: ChatCommand) => void,
): NascentComposer => ({
    draft: session.draft,
    onDraftChange: (text) =>
        send({ type: "edited_draft", conversationId, text }),
    // The first message on THIS conversation tab: the bubble shows at once
    // and chat starts the conversation durably (with the tab's own id) and
    // flushes its pending grounding before sending (§3.1).
    onStartThread: () => intents.submitText(session.draft, conversationId),
    composerStatus: composerStatus(session, context.failureText),
    composerDisabled: context.historicalPlayback,
    onStop: () => intents.stopTurn(conversationId),
    onRetry: () => intents.retryTurn(conversationId),
});

export const ChatFullView = ({
    conversationId,
    model,
    context,
    intents,
    host,
    send,
}: {
    readonly conversationId: string;
    readonly model: ChatState;
    readonly context: ChatViewContext;
    readonly intents: ChatViewIntents;
    readonly host: ChatViewHost;
    readonly send: (command: ChatCommand) => void;
}): ReactNode => {
    const { t } = useLingui();
    const session = chatSessionFor(model.sessions, conversationId);
    const { composerMentions } = model;
    const { refFor, sourceItems, historicalPlayback } = context;
    const landedStatusOf = (id: ResourceId) =>
        chipStatusOf(context.readStatusOf(id));
    // Every reference in the chat opens in the RIGHT PANEL through the one
    // chat intent (`requested_resource_open` → `show-in-panel`), never an
    // editor tab or the library: a material/package card's Öppna, a
    // citation, a tool chip, a read locator (with its page span), an agent
    // link, an @-mention. An id not in the library index opens nothing. The
    // ref is read back through the library index, so a course's module
    // carries its course and opens there (bd-lya).
    const openResource = (link: ResourceRef, target?: FocusTarget): void => {
        const ref = refFor(link.id) ?? link;
        send(
            target === undefined
                ? { type: "requested_resource_open", ref }
                : { type: "requested_resource_open", ref, target },
        );
    };
    const openById = (id: string): void => {
        const ref = refFor(id);
        if (ref !== undefined) openResource(ref);
    };
    // A SENT message's @-reference chips, live or reloaded, resolved through
    // the library and carrying the live enrichment status at RENDER (F83,
    // D7) — never a durable field on AuthorEntry.
    const entries = session.conversation.map(
        (e): ChatEntry =>
            e.type === "author"
                ? {
                      ...e,
                      mentions: sentReferences(e, refFor, landedStatusOf),
                  }
                : e,
    );
    // F83 (SLICE 1): the composer's DISPLAYED mentions fold the optimistic
    // ingest chips on top of the @-picked set. An upload a held
    // send already carries rides its bubble, not the composer.
    const optimisticChips = composerAttachmentsFor(
        conversationId,
        context.ingest,
        context.ingestConversations,
        model.pendingGrounding,
        landedStatusOf,
    )
        .filter((o) => !composerMentions.some((m) => m.id === o.id))
        .filter(
            (o) =>
                session.heldSend.type !== "awaiting_uploads" ||
                !session.heldSend.send.mentions.some((m) => m.id === o.id),
        );
    const optimisticChipIds = new Set(optimisticChips.map((c) => c.id));
    // Reconcile ONLY the real, controllable mentions: drop the optimistic
    // chips (no durable ref) and strip the render-time `status`, so the
    // durable `composerMentions` never stores a transient status.
    const onMentionsChange = (next: readonly ComposerResourceRef[]) =>
        host.changeMentions(
            next
                .filter((n) => !optimisticChipIds.has(n.id))
                .map(
                    (n): ComposerResourceRef => ({
                        kind: n.kind,
                        id: n.id,
                        title: n.title,
                    }),
                ),
        );
    // G3-interim-B1: an interrupted turn that had a material build is
    // demoted from a blind Retry to the calm "created, review it" note —
    // per-step commit means the course almost certainly survived. Only the
    // turn that failed counts: a course made earlier in the conversation
    // says nothing about this failure.
    const baseStatus = composerStatus(session, context.failureText);
    const status: PowerComposerStatus =
        baseStatus.type === "error" &&
        transcriptHasCreatedMaterial(lastTurn(session.conversation))
            ? { type: "created" }
            : baseStatus;
    const openingMoveOffer: OpeningMoveOfferView | undefined =
        shouldShowOpeningMoveOffer({
            hasSource: sourceItems.length > 0,
            transcript: session.conversation,
        })
            ? {
                  onAccept: () => intents.acceptOpeningMove(conversationId),
                  sourceTitle: (context.activeRef?.type === "source"
                      ? context.activeRef
                      : sourceItems[0]
                  )?.title,
              }
            : undefined;
    // The "+" add-a-source affordance (F10 parity with the hero): the same
    // upload path and the same mention vocabulary the `@`-menu uses.
    const pickExistingSource = (source: ComposerResourceRef) =>
        host.changeMentions(
            composerMentions.some((m) => m.id === source.id)
                ? composerMentions
                : [...composerMentions, source],
        );
    return (
        <ChatFull
            conversationId={conversationId}
            entries={entries}
            scope={context.scope}
            anchor={context.anchor}
            drafting={draftingPhaseOf(session)}
            draft={session.draft}
            onDraftChange={(text) =>
                send({ type: "edited_draft", conversationId, text })
            }
            onSubmit={() => intents.submitText(session.draft, conversationId)}
            composerDisabled={historicalPlayback}
            status={status}
            onStop={() => intents.stopTurn(conversationId)}
            onRetry={() => intents.retryTurn(conversationId)}
            // Composer message-history recall (A-11): one command per
            // direction.
            onRecallOlder={() =>
                send({ type: "recalled_older_message", conversationId })
            }
            onRecallNewer={() =>
                send({ type: "recalled_newer_message", conversationId })
            }
            historyNavActive={session.historyNav.type === "at_history"}
            mentions={[...composerMentions, ...optimisticChips]}
            onMentionsChange={onMentionsChange}
            candidates={context.composerCandidates}
            commands={context.chatSlashCommands}
            composerLeadingSlot={
                <ComposerAttachButton
                    candidates={context.composerCandidates}
                    contextItems={mentionsToContextItems(composerMentions)}
                    onUpload={host.openFilePicker}
                    onPick={pickExistingSource}
                    disabled={historicalPlayback}
                />
            }
            onSuggestedResponse={(text) =>
                intents.submitText(text, conversationId)
            }
            onGoToChange={(entryId) =>
                host.goToChange(entryId, session.conversation)
            }
            onAcceptProposal={(proposalId) =>
                intents.acceptProposal(conversationId, proposalId)
            }
            onDiscardProposal={(proposalId) =>
                intents.discardProposal(conversationId, proposalId)
            }
            onOpenMaterial={openById}
            onOpenExport={openById}
            onDownloadExport={host.downloadExport}
            // Re-upload into a source whose content is missing; the restore
            // notice then offers "Continue" in THIS conversation.
            onRequestUpload={(sourceId, sourceTitle) =>
                host.requestUpload(sourceId, sourceTitle, conversationId)
            }
            onOpenSource={openById}
            onOpenResource={openResource}
            // Agent `[title](/library/{id})` links → chips.
            resolveLink={context.resolveLink}
            onSubmitForm={(requestId, answers) =>
                intents.supplyFormAnswer(conversationId, requestId, answers)
            }
            // D42: a `field_sources` question offers the library's sources.
            formSources={sourceItems}
            openingMoveOffer={openingMoveOffer}
            onInterruptionAction={interruptionHandler(
                conversationId,
                session,
                intents,
                host,
                {
                    continue: t`Continue where you left off.`,
                    tryAgain: t`Please try that again.`,
                },
            )}
        />
    );
};
